import {
  DOCUMENT_TYPES,
  INVOICE_STATUSES,
  type DocumentType,
  type InvoiceStatus,
} from '@/shared/api/billing';
import { isIsoDay } from '@/shared/format';

/**
 * `validateSearch` of `/billing/invoices` (`AM-350`): every filter lives in the URL, so a link opens the
 * list already narrowed ("Ver todos" of "Sin cobrar", the documents of a customer) and back and forward
 * keep what the reader chose. A value the list does not know is dropped, never thrown on.
 * `issuedFrom` and `issuedTo` are the first and the last day of issue, the tenant's (`AM-368`): "Ver
 * todo" of "Facturas emitidas" of the board of the auditor opens the list on the days of its range.
 * `AM-412` (D8): "Sin cobrar" (`unpaid`) and "Vencidos" (`overdue`) are filters of their own, beside
 * the state; an old link with `status=unpaid` still opens "Sin cobrar".
 */
export interface InvoicesSearch {
  status?: InvoiceStatus;
  unpaid?: true;
  overdue?: true;
  /**
   * `AM-843` (C33-37): "Facturado" (`billed`), the documents the report "Facturado" adds up, and the currency of a row
   * of it, so a row of the report opens the documents it adds up.
   */
  billed?: true;
  currency?: string;
  documentType?: DocumentType;
  organizationId?: string;
  number?: string;
  /** `AM-443`: the contract, the period and part of the number of the ticket. */
  contractId?: string;
  billingPeriodId?: string;
  ticketNumber?: string;
  /** `AM-443`: the first and the last "Fecha del comprobante". */
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  issuedFrom?: string;
  issuedTo?: string;
}

const isOn = (value: unknown): boolean => value === true || value === 'true';

export function invoicesSearchSchema(search: Record<string, unknown>): InvoicesSearch {
  const result: InvoicesSearch = {};

  const status = search['status'];
  if (typeof status === 'string' && INVOICE_STATUSES.includes(status as InvoiceStatus)) {
    result.status = status as InvoiceStatus;
  }
  if (isOn(search['unpaid']) || status === 'unpaid') {
    result.unpaid = true;
  }
  if (isOn(search['overdue'])) {
    result.overdue = true;
  }
  if (isOn(search['billed'])) {
    result.billed = true;
  }
  const currency = search['currency'];
  if (typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)) {
    result.currency = currency;
  }

  const documentType = search['documentType'];
  if (typeof documentType === 'string' && DOCUMENT_TYPES.includes(documentType as DocumentType)) {
    result.documentType = documentType as DocumentType;
  }

  const organizationId = search['organizationId'];
  if (typeof organizationId === 'string' && organizationId.length > 0) {
    result.organizationId = organizationId;
  }

  const number = search['number'];
  if (typeof number === 'string' && number.trim().length > 0) {
    result.number = number;
  }

  for (const key of ['contractId', 'billingPeriodId'] as const) {
    const value = search[key];
    if (typeof value === 'string' && value.length > 0) {
      result[key] = value;
    }
  }

  const ticketNumber = search['ticketNumber'];
  if (typeof ticketNumber === 'string' && ticketNumber.trim().length > 0) {
    result.ticketNumber = ticketNumber;
  }

  const invoiceDateFrom = search['invoiceDateFrom'];
  if (isIsoDay(invoiceDateFrom)) {
    result.invoiceDateFrom = invoiceDateFrom;
  }

  const invoiceDateTo = search['invoiceDateTo'];
  if (isIsoDay(invoiceDateTo)) {
    result.invoiceDateTo = invoiceDateTo;
  }

  const issuedFrom = search['issuedFrom'];
  if (isIsoDay(issuedFrom)) {
    result.issuedFrom = issuedFrom;
  }

  const issuedTo = search['issuedTo'];
  if (isIsoDay(issuedTo)) {
    result.issuedTo = issuedTo;
  }

  return result;
}
